import React, { useMemo } from 'react';
import { BoxProps, Flex, TextV2 } from '@withjoy/joykit';
import { MapMarker } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { TravelDistancesBetweenPointsResponseFragment } from '@graphql/generated';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { extractTypedDistances } from '@shared/utils/formatting/distance.formatter';
import { useFormatTravelDistance } from '@shared/utils/hooks/useFormatTravelDistance';
import { useIsImperialCultures } from '@shared/utils/hooks/useIsImperialCultures';

export interface DistanceFromVenueProps extends BoxProps {
  distances?: Maybe<TravelDistancesBetweenPointsResponseFragment>;
  milesToVenue?: number | null;
  /** Size variant - affects font size */
  size?: 'sm' | 'md';
  useInterUI?: boolean;
  hasIcon?: boolean;
  iconSize?: number;
  useToWording?: boolean;
  useShortWording?: boolean;
}

export const DistanceFromVenue: React.FC<DistanceFromVenueProps> = ({
  distances,
  milesToVenue,
  color = 'mono10',
  size = 'md',
  useInterUI = true,
  hasIcon = false,
  iconSize = 16,
  useToWording = false,
  useShortWording = false,
  ...props
}) => {
  const { tq } = useTranslation('sharedComponents');

  const isImperialCultures = useIsImperialCultures();
  const formatTravelDistance = useFormatTravelDistance();

  const [accommodationsTravelDistanceExperimentEnabled] = useIsFeatureEnabled('accommodationsTravelDistanceExperiment');

  const formattedDistance = useMemo(() => {
    // Check for distances to enable only for guest and admin accommodations
    if (distances && accommodationsTravelDistanceExperimentEnabled) {
      const { displayDistance } = extractTypedDistances(distances?.commonDistance);

      if (!displayDistance) return null;

      const { formatted, unitLabel } = formatTravelDistance({ value: displayDistance.mi, type: 'distance' });
      const metricFormatted = formatTravelDistance({ value: displayDistance.km, type: 'distance' });

      return isImperialCultures ? { formatted, unitLabel } : metricFormatted;
    }

    if (!milesToVenue) return null;

    return formatTravelDistance({ value: milesToVenue, type: 'distance' });
  }, [accommodationsTravelDistanceExperimentEnabled, distances, formatTravelDistance, isImperialCultures, milesToVenue]);

  if (!formattedDistance) return null;

  const distanceWithUnit = `${formattedDistance.formatted} ${formattedDistance.unitLabel}`;

  let displayDistance;
  if (useShortWording) {
    displayDistance = tq`${distanceWithUnit}`;
  } else {
    if (useToWording) {
      displayDistance = tq`${distanceWithUnit} to venue`;
    } else {
      displayDistance = tq`${distanceWithUnit} from venue`;
    }
  }

  const fontSize = props.fontSize ?? (size === 'sm' ? pxToRem(13) : pxToRem(15));
  const typographyVariant = size === 'sm' ? 'label2' : 'body1';

  return (
    <Flex
      gap={2}
      alignItems="center"
      {...props}
    >
      {hasIcon && <MapMarker size={iconSize} />}
      <TextV2
        typographyVariant={typographyVariant}
        fontSize={fontSize}
        color={color}
        {...(useInterUI && { fontFamily: 'Inter UI' })}
      >
        {displayDistance}
      </TextV2>
    </Flex>
  );
};
